import gsap from 'gsap';
import { reaction } from 'mobx';
import { GAME_SPEED_TYPES } from '../../../constants/gameConstants';
import { gameModuleStore } from '../../store/gameModuleStore';
import { AnimationConfigs } from '../../../constants/reelConstants';
import { SpinIconNumberProvider } from '../../../helpers/spinIconNumberProvider';
import { SlotIcon } from '../slotIcon';
import { iconStates, specialIconKeys } from '../../../constants/symbolsNames';
import { Container } from 'pixi.js';

export class Reel extends Container {
  index = 0;
  canStop = false;
  isForceStop = false;
  isLastReel = false;
  stopNextReel = null;
  reelsContainer = null;
  winContainer = null;
  vector = true;
  stopComplete = true;
  responseReady = false;

  constructor({ index, reelsContainer, slotWinLayer, isLastReel, stopNextReel, reelParams }) {
    super();
    this.index = index;
    this.reelsContainer = reelsContainer;
    this.winContainer = slotWinLayer;
    this.isLastReel = isLastReel;
    this.AnimationConfigs = AnimationConfigs;
    this.reelParams = reelParams;
    this.iconNumberProvider = new SpinIconNumberProvider();
    this.iconsHeight = this.reelParams[index].iconsHeight;
    this.iconsInReel = this.reelParams[index].iconsInReel;
    this.visibleRows = this.reelParams[index].visibleRows;
    this.speedDuration = this.reelParams[index].speedDuration;
    this.iconsWidth = this.reelParams[index].iconsWidth;
    this.iconsSpacing = this.reelParams[index].iconsSpacing;
    this.spacing = this.reelParams[index].spacing;
    this.xPosition = 0;
    this.icons = [];
    this.iconContainers = [];
    this.inStopAnimation = false;
    this.finalDrawCardNumbers = [];
    this.reactionDisposers = [];
    this.initReactions();
    this.initReel();
    const { posStart, posEnd, duration, posEndStop, stopEase, stopDuration } =
      this.checkPositionsAndDuration();
    this.posStart = posStart;
    this.posEnd = posEnd;
    this.duration = duration;
    this.posEndStop = posEndStop;
    this.stopEase = stopEase;
    this.stopDuration = stopDuration;
    this.stopNextReel = stopNextReel;
  }

  // RU: реакции живут на сторе-синглтоне и снимаются в `dispose`: без этого барабан, переживший
  //     свою доску, продолжал бы реагировать на скорость и force stop и запускать твины на
  //     уничтоженных узлах.
  initReactions() {
    this.reactionDisposers.push(
      reaction(
        () => gameModuleStore.spinSpeedType,
        () => {
          const { posStart, posEnd, duration, posEndStop, stopEase, stopDuration } =
            this.checkPositionsAndDuration();
          this.posStart = posStart;
          this.posEnd = posEnd;
          this.duration = duration;
          this.posEndStop = posEndStop;
          this.stopEase = stopEase;
          this.stopDuration = stopDuration;
        }
      ),
      reaction(
        () => gameModuleStore.isForceStop,
        () => {
          if (
            gameModuleStore.isForceStop &&
            !this.stopComplete &&
            !this.inStopAnimation &&
            this.responseReady
          ) {
            this.isForceStop = true;
            this.stopTurboReelAnimation();
          }
        }
      )
    );
  }

  dispose() {
    this.reactionDisposers.forEach((disposeReaction) => disposeReaction());
    this.reactionDisposers = [];
    // RU: твина ещё нет, если доску размонтировали до первого спина.
    this.animation?.kill();
  }

  initReel() {
    this.initContainerPosition();
    this.addReelIcons(gameModuleStore.initialMatrix[this.index]);
    this.fillIconsFinalPositionsConfigArray();
    this.initIconsPositions();
  }

  initContainerPosition() {
    this.xPosition = this.index * (this.iconsWidth + (this.index === 0 ? 0 : this.spacing));
    this.xPosition += this.reelParams[this.index].offsetForContainerBoardWidth;
    this.reelParams[this.index].finalPositionX = this.xPosition;
    this.startPositionY =
      -(this.iconsHeight + this.iconsSpacing) * this.visibleRows - this.iconsSpacing * 2;
    this.reelParams[this.index].finalPositionY = -this.iconsHeight * (this.visibleRows - 1);
    this.setContainerPos(this.startPositionY, this.xPosition);
    this.reelsContainer.addChild(this);
  }

  setContainerPos(posY, posX = this.xPosition) {
    this.position.set(posX, posY);
  }

  createIconContainers() {
    for (let i = 0; i < this.iconsInReel; i++) {
      const tempContainer = new Container();
      tempContainer.name = 'iconContainer';
      this.iconContainers.push(tempContainer);
      this.addChild(tempContainer);
    }
  }

  initIconsPositions() {
    this.icons.forEach((icon, index) => {
      icon.setWinContainerPositions(
        this.reelParams[this.index].iconsFinalPositions.inWinContainer[index]
      );
    });
  }

  addReelIcons(matrix) {
    const updatedMatrix = this.changeMatrixMode(matrix);
    this.createIconContainers();
    for (let i = 0; i < this.iconContainers.length; i++) {
      const tempIcon = this.createSymbol(
        this.iconContainers[i],
        this.iconsHeight + this.iconsSpacing,
        updatedMatrix[i],
        this.index,
        i
      );
      this.icons.push(tempIcon);
    }
  }

  createSymbol(container, height, key, index, elementIndex) {
    return new SlotIcon({
      parent: container,
      rowIndex: elementIndex,
      winParent: this.winContainer,
      styles: { x: 0, y: elementIndex * height },
      reelIndex: index,
      key,
    });
  }

  changeMatrixMode(matrix) {
    const optionalMatrix = [];
    for (let i = 0; i < this.visibleRows; i++) {
      optionalMatrix.push(this.iconNumberProvider.getNext());
    }
    return [...optionalMatrix, ...matrix, ...optionalMatrix];
  }

  startSpin() {
    this.vector = gameModuleStore.spinDirection === 1;
    this.inStopAnimation = false;
    this.stopComplete = false;
    this.isForceStop = false;
    this.responseReady = false;
    this.rotateLoop();
  }

  rotateLoop() {
    this.setRandomSymbolsOnStart();
    const isFast = gameModuleStore.spinSpeedType === GAME_SPEED_TYPES.fast;
    if (!isFast) {
      this.setContainerPos(this.vector ? this.posStart : this.posEnd);
    } else {
      this.setContainerPos(
        this.vector ? this.posStart : this.posEnd + this.visibleRows * this.iconsHeight
      );
    }
    this.animation = gsap.to(this, {
      duration: this.duration,
      repeatDelay: 0.0001,
      immediateRender: true,
      pixi: { y: this.vector ? this.posEnd : isFast ? this.posEnd : this.posStart },
      ease: 'linear',
      repeat: -1,
      onRepeat: () => {
        this.loopBeginAnimation();
      },
    });
  }

  setRandomSymbolsOnStart() {
    const totalSymbols = this.iconsInReel;
    const visibleCount = this.visibleRows;
    const symbolsVisible = new Array(visibleCount);

    for (let i = visibleCount; i < visibleCount * 2; i++) {
      symbolsVisible[i - visibleCount] = this.icons[i].key;
      this.icons[i].changeState(iconStates.blur);
    }

    if (this.vector) {
      for (let i = visibleCount * 2, j = 0; i < totalSymbols; i++, j++) {
        this.icons[i].changeIcon(symbolsVisible[j], 'blur');
      }
      const replacementLimit = totalSymbols - visibleCount;
      for (let i = 0; i < replacementLimit; i++) {
        this.icons[i].changeIcon(this.iconNumberProvider.getNext(), 'blur');
      }
    } else {
      for (let i = 0; i < visibleCount; i++) {
        this.icons[i].changeIcon(symbolsVisible[i], 'blur');
      }
      for (let i = visibleCount; i < totalSymbols; i++) {
        this.icons[i].changeIcon(this.iconNumberProvider.getNext(), 'blur');
      }
    }
  }

  // RU: раскрытие wild на весь барабан: каждый не-wild символ окна становится wild в позе
  //     `winExpand` и после первого прохода крутит её по кругу. Промис резолвится ВСЕГДА: если
  //     барабан уже весь из wild, раскрывать нечего — прежде промис тогда не резолвился никогда,
  //     и раунд висел без единой ошибки (до forceStop).
  startWildAnimation = () => {
    const iconsToAnimate = this.icons.filter(
      (icon, index) =>
        index >= this.visibleRows &&
        index < this.visibleRows * 2 &&
        icon.iconNumber !== specialIconKeys.wild
    );
    if (!iconsToAnimate.length) return Promise.resolve();
    gameModuleStore.soundService.play('WildOpeningSound');
    return Promise.all(
      iconsToAnimate.map(
        (icon) =>
          new Promise((res) => {
            icon.changeIcon(specialIconKeys.wild, iconStates.winExpand);
            gameModuleStore.setWildIcons(icon);
            icon.icon.onceAnimationEvent(
              'complete',
              () => {
                icon.icon.play(iconStates.winExpand, true);
                res();
              },
              iconStates.winExpand
            );
          })
      )
    );
  };

  startWinAnimation = (statesConfig) => {
    const animations = [];
    this.icons.forEach((icon, index) => {
      if (icon.rowIndex >= this.visibleRows && icon.rowIndex < this.visibleRows * 2) {
        const state = statesConfig[index - this.visibleRows];
        gameModuleStore.setWinAnimations(icon);
        animations.push(
          new Promise((res) => {
            icon.changeState(state);
            icon.icon.onceAnimationEvent('complete', () => res(), state);
          })
        );
      }
    });
    return animations;
  };

  loopBeginAnimation = () => {
    if (this.canStop && gameModuleStore.spinSpeedType !== GAME_SPEED_TYPES.turbo) {
      this.stopReelAnimation();
    }
    if (!this.inStopAnimation) {
      if (gameModuleStore.spinSpeedType === GAME_SPEED_TYPES.standard) {
        this.createRandomSymbolsOnLoopMode({
          slice: [0, this.visibleRows],
          count: this.visibleRows * 2,
        });
      } else if (gameModuleStore.spinSpeedType === GAME_SPEED_TYPES.fast) {
        this.createRandomSymbolsOnLoopMode({
          slice: [this.visibleRows, this.visibleRows * 2],
          count: this.visibleRows * 2,
        });
      } else if (gameModuleStore.spinSpeedType === GAME_SPEED_TYPES.turbo) {
        this.createRandomSymbolsOnLoopMode({
          slice: [0, this.visibleRows],
          count: this.visibleRows * 2,
        });
      }
    }
  };

  checkPositionsAndDuration() {
    switch (gameModuleStore.spinSpeedType) {
      case GAME_SPEED_TYPES.standard:
        return {
          posStart: this.startPositionY - this.visibleRows * this.iconsHeight,
          posEnd: this.startPositionY + this.visibleRows * this.iconsHeight,
          duration: this.speedDuration,
          stopDuration: this.speedDuration * 1.35,
          stopEase: this.AnimationConfigs.stopNormal,
          posEndStop: this.startPositionY,
        };
      case GAME_SPEED_TYPES.fast:
        return {
          posStart: this.startPositionY - this.visibleRows * this.iconsHeight,
          posEnd: this.startPositionY,
          duration: this.speedDuration / 3,
          stopDuration: this.speedDuration * 1.5,
          stopEase: this.AnimationConfigs.stopFast,
          posEndStop: this.startPositionY,
        };
      case GAME_SPEED_TYPES.turbo:
        return {
          posStart: this.startPositionY - this.visibleRows * this.iconsHeight,
          posEnd: this.startPositionY + this.visibleRows * this.iconsHeight,
          duration: this.speedDuration,
          stopDuration: this.speedDuration,
          stopEase: this.AnimationConfigs.stopTurbo,
          posEndStop: this.startPositionY,
        };
      default:
        return {
          posStart: this.startPositionY,
          posEnd: this.startPositionY,
          duration: this.speedDuration,
          stopDuration: this.speedDuration,
          stopEase: 'linear',
          posEndStop: this.startPositionY,
        };
    }
  }

  createRandomSymbolsOnLoopMode(symbolsConf) {
    const getIconKey = (icon) => icon.key;

    const arr = this.vector
      ? this.icons.slice(symbolsConf.slice[0], symbolsConf.slice[1]).map(getIconKey)
      : this.icons
          .slice(this.iconsInReel - symbolsConf.slice[1], this.iconsInReel - symbolsConf.slice[0])
          .map(getIconKey);

    const [startIdx, endIdx] = this.vector
      ? [this.visibleRows * 2, this.visibleRows * 3]
      : [0, this.visibleRows];

    for (let i = startIdx; i < endIdx; i += 1) {
      this.icons[i].changeIcon(arr[i - startIdx], 'blur');
    }

    const randomSlice = this.vector
      ? this.icons.slice(0, symbolsConf.count)
      : this.icons.slice(this.visibleRows, this.visibleRows + symbolsConf.count);

    randomSlice.forEach((icon) => {
      icon.changeIcon(this.iconNumberProvider.getNext(), 'blur');
    });
  }

  setResponseMatrixForStop() {
    const isTurboOrStandard =
      gameModuleStore.spinSpeedType === GAME_SPEED_TYPES.turbo ||
      gameModuleStore.spinSpeedType === GAME_SPEED_TYPES.standard;
    const reelIcons = this.icons.slice(0, this.visibleRows * 3);
    const copySymbols = [];

    if (this.vector) {
      reelIcons.forEach((icon, i) => {
        let iconNumber;
        if (i < this.visibleRows) {
          if (isTurboOrStandard) {
            copySymbols.push(this.icons[i].iconNumber);
          }
          iconNumber = this.iconNumberProvider.getNext();
        } else if (i < this.visibleRows * 2) {
          if (!isTurboOrStandard) {
            copySymbols.push(this.icons[i].key);
          }
          iconNumber = this.finalDrawCardNumbers[i - this.visibleRows];
        } else {
          iconNumber = copySymbols[i - this.visibleRows * 2];
        }
        icon.changeIcon(iconNumber, 'blur');
      });
    } else {
      for (let i = this.visibleRows * 2; i < this.iconsInReel; i++) {
        if (isTurboOrStandard) {
          copySymbols.push(this.icons[i].iconNumber);
        }
        this.icons[i].changeIcon(this.iconNumberProvider.getNext(), iconStates.blur);
      }
      for (let i = this.visibleRows; i < this.visibleRows * 2; i++) {
        if (!isTurboOrStandard) {
          copySymbols.push(this.icons[i].key);
        }
        const iconNumber = this.finalDrawCardNumbers[i - this.visibleRows];
        this.icons[i].changeIcon(iconNumber);
      }
      for (let i = 0; i < this.visibleRows; i++) {
        this.icons[i].changeIcon(copySymbols[i], 'blur');
      }
    }
  }

  setSymbolsNonBlur() {
    for (let i = this.visibleRows - 1; i < this.visibleRows * 2 + 1; i++) {
      this.icons[i].changeState(iconStates.idle);
    }
  }

  stopReelAnimation() {
    if (this.inStopAnimation) return;
    this.inStopAnimation = true;
    const isFast = gameModuleStore.spinSpeedType === GAME_SPEED_TYPES.fast;
    this.setResponseMatrixForStop();
    this.setSymbolsNonBlur();
    if (this.animation) this.animation.kill();
    this.animation = gsap.to(this, {
      pixi: { y: this.posEndStop },
      duration: this.stopDuration,
      ease: this.stopEase,
      onStart: async () => {
        if (!this.isLastReel && !this.isForceStop) this.stopNextReel.next();
        this.playSoundForSpecialSymbols();
        await new Promise((resolve) => setTimeout(resolve, isFast ? 160 : 120));
        gameModuleStore.soundService.play('ReelStop');
      },
      onComplete: () => {
        this.stopComplete = true;
        if (this.isLastReel) {
          this.animation.kill();
          gameModuleStore.handleReelAnimationEnd();
        }
        this.inStopAnimation = false;
      },
    });
  }

  playSoundForSpecialSymbols() {
    if (this.finalDrawCardNumbers.some((number) => number === specialIconKeys.wild)) {
      gameModuleStore.soundService.play('WildSymbol');
    }
    if (this.finalDrawCardNumbers.some((number) => number === specialIconKeys.scatter1)) {
      if (this.index <= 2) {
        gameModuleStore.setScatterCount('scatter1');
        gameModuleStore.soundService.play('ScatterWin1');
      } else if (gameModuleStore.scatter1Count && this.index === 3) {
        gameModuleStore.setScatterCount('scatter1');
        gameModuleStore.soundService.play('ScatterWin1');
      } else if (gameModuleStore.scatter1Count >= 2 && this.index === 4) {
        gameModuleStore.setScatterCount('scatter1');
        gameModuleStore.soundService.play('ScatterWin1');
      }
    }

    if (this.finalDrawCardNumbers.some((number) => number === specialIconKeys.scatter2)) {
      if (this.index === 0) {
        gameModuleStore.setScatterCount('scatter2');
        gameModuleStore.soundService.play('ScatterWin2');
      } else if (gameModuleStore.scatter2Count === 1 && this.index === 2) {
        gameModuleStore.setScatterCount('scatter2');
        gameModuleStore.soundService.play('ScatterWin2');
      } else if (gameModuleStore.scatter2Count === 2 && this.index === 4) {
        gameModuleStore.setScatterCount('scatter2');
        gameModuleStore.soundService.play('ScatterWin2');
      }
    }
  }

  stopTurboReelAnimation(isFromRandomPosition = true) {
    if (this.inStopAnimation) return;
    this.inStopAnimation = true;
    this.setResponseMatrixForStop();
    this.setSymbolsNonBlur();
    const isFast = gameModuleStore.spinSpeedType === GAME_SPEED_TYPES.fast;
    if (this.animation) this.animation.kill();
    if (isFromRandomPosition) {
      this.setContainerPos(
        this.vector
          ? this.posStart
          : isFast
          ? this.posEnd + this.visibleRows * this.iconsHeight
          : this.posEnd
      );
    }
    this.animation = gsap.to(this, {
      pixi: { y: this.posEndStop },
      duration: this.stopDuration,
      ease: this.stopEase,
      onStart: async () => {
        this.playSoundForSpecialSymbols();
        await new Promise((resolve) => setTimeout(resolve, 50));
        if (!this.isLastReel && this.isForceStop) this.stopNextReel.next();
        if (gameModuleStore.isForceStop) {
          gameModuleStore.soundService.play('ReelStop');
        } else {
          if (!this.index) {
            gameModuleStore.soundService.play('ReelStop');
          }
        }
      },
      onComplete: () => {
        this.stopComplete = true;
        this.inStopAnimation = false;
        if (this.isLastReel) {
          gameModuleStore.handleReelAnimationEnd();
        }
      },
    });
  }

  move(moveAmount) {
    this.y += moveAmount;
  }

  initFromResponseData(matrix) {
    const responseData = matrix && matrix[this.index];
    if (!responseData) return;
    this.finalDrawCardNumbers = [...responseData];
    this.responseReady = true;
  }

  fillIconsFinalPositionsConfigArray() {
    const reel = this.reelParams[this.index];
    reel.iconsFinalPositions.inWinContainer = [];
    reel.iconsFinalPositions.inReel = [];
    for (let i = 0; i < this.iconsInReel; i++) {
      const xInReel = this.icons[i].xPosInReel;
      const yInReel = this.icons[i].yPosInReel;
      reel.iconsFinalPositions.inReel.push({ xInReel, yInReel });
    }
    for (let i = 0; i < this.iconsInReel; i++) {
      const xInWin = reel.finalPositionX;
      const yInWin =
        (this.icons[i].rowIndex - reel.visibleRows) *
        (this.reelParams[this.icons[i].reelIndex].iconsHeight -
          this.reelParams[this.icons[i].reelIndex].iconsSpacing);
      reel.iconsFinalPositions.inWinContainer.push({ xInWin, yInWin });
    }
  }
}
